/**
 * Maple route tree, composable under any parent path.
 *
 * Flow-centric (per local/maplebridge/mockup.html): Overview shows the flow
 * lanes; each lane opens a per-flow hub (Procare = remittance batches + 835
 * reconcile; MapleBridge/Meritain = the multi-stage Bill→Collect→Pay pipeline).
 * Claims is a global explorer; Ingest Queries shows the per-flow extract SQL.
 * Remittance searches the raw Procare 835 upstream of Maple. Enrollment
 * reconciles the roster (Vera Ops MapleBridge groups vs the Vista groups the
 * ingest covers). Load History is every run that has ever written claims,
 * including the in-app 835 load the Procare hub drives.
 *
 * Maple is Postgres-backed (maplebridge schema); its own data is BFF-mediated
 * (`/api/maple/*`), never Dataverse, with two deliberate exceptions:
 * Remittance is served by the BFF live from the Fabric warehouse, and the
 * Enrollment page reads the Vera Ops side (group services, partner IDs)
 * through the shared dataverseClient in the browser, where the delegated
 * session and org switcher live.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { AppShell, type NavEntry } from '@widgets/app-shell';
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';
import {
  BoardRegular,
  ReceiptMoneyRegular,
  BookDatabaseRegular,
  DocumentTableRegular,
  CircleRegular,
  OrganizationRegular,
  HistoryRegular,
} from '@fluentui/react-icons';

const OverviewPage = lazy(() => import('@maple/pages/overview').then(m => ({ default: m.OverviewPage })));
const FlowPage = lazy(() => import('@maple/pages/flow').then(m => ({ default: m.FlowPage })));
const WorkspacePage = lazy(() => import('@maple/pages/workspace').then(m => ({ default: m.WorkspacePage })));
const ClaimsPage = lazy(() => import('@maple/pages/claims').then(m => ({ default: m.ClaimsPage })));
const RemittancePage = lazy(() => import('@maple/pages/remittance').then(m => ({ default: m.RemittancePage })));
const RemittanceDetailPage = lazy(() => import('@maple/pages/remittance-detail').then(m => ({ default: m.RemittanceDetailPage })));
const IngestQueriesPage = lazy(() => import('@maple/pages/ingest-queries').then(m => ({ default: m.IngestQueriesPage })));
const EnrollmentPage = lazy(() => import('@maple/pages/enrollment').then(m => ({ default: m.EnrollmentPage })));
const LoadHistoryPage = lazy(() => import('@maple/pages/load-history').then(m => ({ default: m.LoadHistoryPage })));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

const MapleBasePathContext = createContext<string>('');

/** The path Maple is mounted under ('' standalone, '/maple' in the shell), for building in-module links. */
export function useMapleBasePath(): string {
  return useContext(MapleBasePathContext);
}

function buildNavItems(basePath: string): NavEntry[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  return [
    { path: p('/'), label: 'Overview', icon: BoardRegular },
    { kind: 'section', label: 'Maple Flows' },
    { path: p('/flow/procare'), label: 'Cloud / Procare', icon: CircleRegular },
    { path: p('/flow/maplebridge'), label: 'MapleBridge', icon: CircleRegular },
    { path: p('/flow/meritain'), label: 'Meritain', icon: CircleRegular },
    { kind: 'section', label: 'Data' },
    { path: p('/claims'), label: 'Claims', icon: ReceiptMoneyRegular },
    { path: p('/remittance'), label: 'Remittance (835)', icon: DocumentTableRegular },
    { kind: 'section', label: 'Admin' },
    { path: p('/enrollment'), label: 'Enrollment', icon: OrganizationRegular },
    { path: p('/load-history'), label: 'Load History', icon: HistoryRegular },
    { path: p('/ingest-queries'), label: 'Ingest Queries', icon: BookDatabaseRegular },
  ];
}

export function MapleRootLayout() {
  const basePath = useContext(MapleBasePathContext);
  const navItems = buildNavItems(basePath);
  return (
    <ProtectedRoute appRolePrefix="Maple">
      <ErrorBoundary>
        <AppShell
          appId="maple"
          appTitle="Maple"
          navItems={navItems}
        >
          <Outlet />
        </AppShell>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

/**
 * Returns the Maple route tree, with sidebar links prefixed for the mount path.
 * Standalone: `mapleRoutes()` at repo root. Shell: `mapleRoutes('/maple')`.
 */
export function mapleRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <MapleBasePathContext.Provider value={basePath}>
          <MapleRootLayout />
        </MapleBasePathContext.Provider>
      ),
      children: [
        { index: true, element: <LazyPage><OverviewPage /></LazyPage> },
        { path: 'flow/:flow', element: <LazyPage><FlowPage /></LazyPage> },
        { path: 'flow/:flow/stage/:stage', element: <LazyPage><WorkspacePage /></LazyPage> },
        // One 835 batch: its reconciliation, gaps, wires and decision trail.
        { path: 'flow/procare/remittance/:id', element: <LazyPage><RemittanceDetailPage /></LazyPage> },
        { path: 'claims', element: <LazyPage><ClaimsPage /></LazyPage> },
        { path: 'remittance', element: <LazyPage><RemittancePage /></LazyPage> },
        { path: 'enrollment', element: <LazyPage><EnrollmentPage /></LazyPage> },
        { path: 'load-history', element: <LazyPage><LoadHistoryPage /></LazyPage> },
        { path: 'ingest-queries', element: <LazyPage><IngestQueriesPage /></LazyPage> },
        { path: '*', element: <Navigate to={basePath || '/'} replace /> },
      ],
    },
  ];
}
